import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { Toggle } from '@zola/zola-ui/src/components/Toggle';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { MORE_FILTER_LIST_DESKTOP } from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/constants';
import ProvisionalSideDrawer from '../../ProvisionalComponents/ProvisionalSideDrawer';

import styles from './styles.module.less';
import { ActiveFilters } from '../FilterTypes';
import { existenceCheck } from '../../utils';

const MoreFilters = ({
  cardCatalogBusy,
  activeFilters,
  updateMultipleFilters,
  onClose,
  displayMoreFilters,
  hasThirdPartyDesigns,
  hasMultipleOrientations,
  isVowRenewal,
}: MoreFiltersProps): JSX.Element => {
  const [localFilters, setLocalFilters] = useState({
    is_landscape: activeFilters.is_landscape,
    is_portrait: activeFilters.is_portrait,
    featured_artist: activeFilters.featured_artist,
    is_vow_renewal: activeFilters.is_vow_renewal,
  } as MoreFiltersSubset);

  const noLocalFilterSelected =
    MORE_FILTER_LIST_DESKTOP.filter(filterKey => {
      const keyedFilter = filterKey as keyof MoreFiltersSubset;
      const currentValue = localFilters[keyedFilter];
      return existenceCheck(currentValue);
    }).length < 1;

  useEffect(() => {
    setLocalFilters({
      is_landscape: activeFilters.is_landscape,
      is_portrait: activeFilters.is_portrait,
      featured_artist: activeFilters.featured_artist,
      is_vow_renewal: activeFilters.is_vow_renewal,
    });
  }, [activeFilters, displayMoreFilters]);

  function updateLocalFilter(newVal: Partial<MoreFiltersSubset>) {
    setLocalFilters({
      ...localFilters,
      ...newVal,
    });
  }

  function applyFilter() {
    // if no change close without updating filters
    if (
      localFilters.is_landscape === activeFilters.is_landscape &&
      localFilters.is_portrait === activeFilters.is_portrait &&
      localFilters.featured_artist === activeFilters.featured_artist &&
      localFilters.is_vow_renewal === activeFilters.is_vow_renewal
    ) {
      onClose();
    } else {
      updateMultipleFilters({
        is_landscape: localFilters.is_landscape,
        is_portrait: localFilters.is_portrait,
        featured_artist: localFilters.featured_artist,
        is_vow_renewal: localFilters.is_vow_renewal,
      });
      onClose();
    }
  }

  function clearLocalFilters() {
    updateLocalFilter({
      is_landscape: false,
      is_portrait: false,
      featured_artist: false,
      is_vow_renewal: false,
    });
  }

  return (
    <ProvisionalSideDrawer
      className={styles.sidedrawerOverride}
      displayDrawer={displayMoreFilters}
      onClose={onClose}
      header="More Filters"
      width="420px"
    >
      <div className={styles.sidebarContent}>
        {isVowRenewal && (
          <>
            <div className={styles.filterTitle}>Reception Only</div>
            <div className={cx(styles.filterSection, styles.toggleContainer)}>
              <div className={styles.toggleText}>
                Only show elopement &#38; vow renewal designs.
              </div>
              <Toggle
                id="reception-only"
                input={{
                  value: localFilters.is_vow_renewal,
                  onChange: () =>
                    updateLocalFilter({ is_vow_renewal: !localFilters.is_vow_renewal }),
                }}
                ariaLabel="Reception Only"
                withIcons
              />
            </div>
          </>
        )}
        {hasMultipleOrientations && (
          <>
            <div className={styles.filterTitle}>Orientation</div>
            <div className={styles.filterSection}>
              <CheckboxField
                className={styles.filterCheckbox}
                label="Landscape"
                id="landscape"
                type="checkbox"
                checked={localFilters.is_landscape}
                onChange={() => updateLocalFilter({ is_landscape: !localFilters.is_landscape })}
                meta={{ touched: true }}
              />
              <CheckboxField
                className={styles.filterCheckbox}
                label="Portrait"
                id="portrait"
                type="checkbox"
                checked={localFilters.is_portrait}
                onChange={() => updateLocalFilter({ is_portrait: !localFilters.is_portrait })}
                meta={{ touched: true }}
              />
            </div>
          </>
        )}
        {hasThirdPartyDesigns && (
          <>
            <div className={styles.filterTitle}>Featured Artist</div>
            <div className={cx(styles.filterSection, styles.toggleContainer)}>
              <div className={styles.toggleText}>
                Only show designs created by our featured artists.
              </div>
              <Toggle
                id="featured-artist-toggle-more"
                input={{
                  value: localFilters.featured_artist,
                  onChange: () =>
                    updateLocalFilter({ featured_artist: !localFilters.featured_artist }),
                }}
                ariaLabel="Featured Artists"
                withIcons
              />
            </div>
          </>
        )}
      </div>
      <div className={styles.filterActions}>
        <ButtonV3
          className={cx(styles.clearBtn, {
            [styles.clearBtnDisabled]: noLocalFilterSelected,
          })}
          variant="secondary"
          onClick={clearLocalFilters}
          disabled={noLocalFilterSelected}
        >
          Clear
        </ButtonV3>
        <ButtonV3 disabled={cardCatalogBusy} onClick={applyFilter}>
          {cardCatalogBusy ? 'Please wait...' : 'Apply'}
        </ButtonV3>
      </div>
    </ProvisionalSideDrawer>
  );
};

type MoreFiltersSubset = Pick<
  ActiveFilters,
  'is_landscape' | 'is_portrait' | 'featured_artist' | 'is_vow_renewal'
>;

type MoreFiltersProps = {
  cardCatalogBusy: boolean;
  activeFilters: MoreFiltersSubset;
  updateMultipleFilters: (allFilters: Partial<MoreFiltersSubset>) => void;
  onClose: () => void;
  displayMoreFilters: boolean;
  hasThirdPartyDesigns: boolean;
  hasMultipleOrientations: boolean;
  isVowRenewal: boolean;
};

export default MoreFilters;
